import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { useT } from '../i18n';
import { HORIZON_LABELS, type DimLang, type Horizon } from '../../shared/scoreDimensions';

interface UsageCard {
  id: number; name: string; pdf_card_number: number | null; category_id: number | null; active: boolean; metalabel: string | null;
  category_name: string | null; category_color: string | null; category_number: number | null;
  picks: number; participants: number; sessions: number; first_pick: string | null; last_pick: string | null; with_context: number;
}
interface UsageCategory { id: number; number: number; name: string; color: string; picks: number; participants: number; sessions: number }
interface Usage {
  since: string | null;
  totals: { picks: number; entries: number; sessions: number; participants: number; first_date: string | null; last_date: string | null; supplementary_picks: number };
  categories: UsageCategory[];
  cards: UsageCard[];
  never_picked: { id: number; name: string; pdf_card_number: number | null; category_name: string | null }[];
  pairs: { topic_a: number; topic_b: number; together: number; name_a: string; name_b: string }[];
  horizons: { horizon: Horizon; picks: number }[];
}

type SortKey = 'picks' | 'participants' | 'sessions' | 'last_pick' | 'number';

function isoMonthsAgo(months: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * How the deck is actually used (PV ae45 §3): which cards and colours come
 * back, which never do, which cards travel together — the material for the
 * restructuring of the deck (~20 base cards, duplicates merged).
 */
export function CardUsagePage() {
  const { t, lang } = useT();
  const dl = lang as DimLang;
  const [period, setPeriod] = useState<'all' | '12m' | '6m'>('all');
  const [data, setData] = useState<Usage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortKey>('picks');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    const since = period === '12m' ? isoMonthsAgo(12) : period === '6m' ? isoMonthsAgo(6) : null;
    setData(null); setError(null);
    api.get<Usage>(`/api/cards/usage${since ? `?since=${since}` : ''}`)
      .then(setData)
      .catch((e) => setError(String(e)));
  }, [period]);

  const formatShort = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(lang === 'fr' ? 'fr-CH' : 'en-CH', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const cards = useMemo(() => {
    if (!data) return [];
    const q = filter.trim().toLowerCase();
    const list = data.cards.filter((c) => !q || c.name.toLowerCase().includes(q) || (c.category_name ?? '').toLowerCase().includes(q));
    const by: Record<SortKey, (a: UsageCard, b: UsageCard) => number> = {
      picks: (a, b) => b.picks - a.picks,
      participants: (a, b) => b.participants - a.participants,
      sessions: (a, b) => b.sessions - a.sessions,
      last_pick: (a, b) => (b.last_pick ?? '').localeCompare(a.last_pick ?? ''),
      number: (a, b) => (a.pdf_card_number ?? 999) - (b.pdf_card_number ?? 999),
    };
    return [...list].sort(by[sort]);
  }, [data, filter, sort]);

  const maxCat = data ? Math.max(1, ...data.categories.map((c) => c.picks)) : 1;
  const maxCard = data ? Math.max(1, ...data.cards.map((c) => c.picks)) : 1;

  return (
    <div className="usage">
      <div className="page-header">
        <div>
          <h1 style={{ margin: 0 }}>{t('usage.title')}</h1>
          <div className="page-header-meta">{t('usage.intro')}</div>
        </div>
        <div className="scope-toggle" role="radiogroup" aria-label={t('usage.period')}>
          {(['all', '12m', '6m'] as const).map((p) => (
            <button key={p} type="button" role="radio" aria-checked={period === p}
              className={`scope-toggle-btn ${period === p ? 'scope-toggle-btn--active' : ''}`}
              onClick={() => setPeriod(p)}>
              {t(`usage.period.${p}`)}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="empty-state" style={{ color: 'var(--err)' }}>{error}</div>}
      {!data && !error && <div className="empty-state">{t('common.loading')}</div>}

      {data && (
        <>
          <div className="usage-totals">
            <div className="usage-kpi"><strong>{data.totals.picks}</strong><span>{t('usage.kpi.picks')}</span></div>
            <div className="usage-kpi"><strong>{data.totals.entries}</strong><span>{t('usage.kpi.entries')}</span></div>
            <div className="usage-kpi"><strong>{data.totals.sessions}</strong><span>{t('usage.kpi.sessions')}</span></div>
            <div className="usage-kpi"><strong>{data.totals.participants}</strong><span>{t('usage.kpi.participants')}</span></div>
            <div className="usage-kpi"><strong>{data.totals.picks > 0 ? Math.round((data.totals.supplementary_picks / data.totals.picks) * 100) : 0} %</strong><span>{t('usage.kpi.supplementary')}</span></div>
            <div className="usage-kpi"><strong>{data.never_picked.length}</strong><span>{t('usage.kpi.never')}</span></div>
          </div>

          <section className="usage-section">
            <h2 className="pd-section-title">{t('usage.byColour')}</h2>
            <table className="usage-table">
              <tbody>
                {data.categories.map((c) => (
                  <tr key={c.id}>
                    <td className="usage-cat-name"><span className="history-grid-row-dot" style={{ background: c.color }} />{c.number}. {c.name}</td>
                    <td className="usage-bar-cell"><span className="usage-bar" style={{ width: `${(c.picks / maxCat) * 100}%`, background: c.color }} /></td>
                    <td className="usage-num">{c.picks}</td>
                    <td className="usage-num usage-muted">{c.participants} {t('usage.col.participantsShort')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.horizons.length > 0 && (
              <div className="usage-muted" style={{ marginTop: 8 }}>
                {t('usage.horizons')} {data.horizons.map((h) => `${HORIZON_LABELS[h.horizon][dl]} ${h.picks}`).join(' · ')}
              </div>
            )}
          </section>

          <section className="usage-section">
            <div className="usage-section-head">
              <h2 className="pd-section-title">{t('usage.byCard')}</h2>
              <input className="field-input usage-filter" placeholder={t('cards.searchPlaceholder')} value={filter} onChange={(e) => setFilter(e.target.value)} />
              <select className="field-input usage-sort" value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
                <option value="picks">{t('usage.sort.picks')}</option>
                <option value="participants">{t('usage.sort.participants')}</option>
                <option value="sessions">{t('usage.sort.sessions')}</option>
                <option value="last_pick">{t('usage.sort.last')}</option>
                <option value="number">{t('usage.sort.number')}</option>
              </select>
            </div>
            <table className="usage-table">
              <thead>
                <tr>
                  <th>{t('cardver.number')}</th>
                  <th>{t('cardver.name')}</th>
                  <th></th>
                  <th className="usage-num">{t('usage.col.picks')}</th>
                  <th className="usage-num">{t('usage.col.participants')}</th>
                  <th className="usage-num">{t('usage.col.sessions')}</th>
                  <th className="usage-num">{t('usage.col.context')}</th>
                  <th>{t('usage.col.last')}</th>
                </tr>
              </thead>
              <tbody>
                {cards.map((c) => (
                  <tr key={c.id} className={c.active === false ? 'usage-row--archived' : ''}>
                    <td className="usage-num">{c.pdf_card_number ?? '—'}</td>
                    <td><span className="history-grid-row-dot" style={{ background: c.category_color ?? 'var(--ink-3)' }} />{c.name}{c.active === false && <span className="badge badge-muted" style={{ marginLeft: 6 }}>{t('cardver.archived')}</span>}</td>
                    <td className="usage-bar-cell"><span className="usage-bar" style={{ width: `${(c.picks / maxCard) * 100}%`, background: c.category_color ?? 'var(--ink-3)' }} /></td>
                    <td className="usage-num">{c.picks}</td>
                    <td className="usage-num">{c.participants}</td>
                    <td className="usage-num">{c.sessions}</td>
                    <td className="usage-num">{c.picks > 0 ? `${Math.round((c.with_context / c.picks) * 100)} %` : '—'}</td>
                    <td className="usage-muted">{c.last_pick ? formatShort(c.last_pick) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="usage-section">
            <h2 className="pd-section-title">{t('usage.pairs')}</h2>
            <div className="usage-muted" style={{ marginBottom: 8 }}>{t('usage.pairsHelp')}</div>
            <ul className="pt-list">
              {data.pairs.map((p) => (
                <li key={`${p.topic_a}-${p.topic_b}`}><strong>{p.together}×</strong> {p.name_a} + {p.name_b}</li>
              ))}
            </ul>
          </section>

          <section className="usage-section">
            <h2 className="pd-section-title">{t('usage.never')}</h2>
            {data.never_picked.length === 0
              ? <div className="usage-muted">{t('usage.neverNone')}</div>
              : <ul className="pt-list">{data.never_picked.map((c) => <li key={c.id}>{c.pdf_card_number != null ? `${c.pdf_card_number}. ` : ''}{c.name} <span className="usage-muted">· {c.category_name ?? t('evo.oldDeck')}</span></li>)}</ul>}
          </section>
        </>
      )}
    </div>
  );
}
